import { html, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Overlay, ColorTypes, Container } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { markdownToHtml, markdownContentStyles } from '@omegagrid/markdown';
import rp, { AppComponent, formatDateTime, helpers, Polling } from "@reporting/rp-app-host";
import rc from '@reporting/rp-app-host';
import { style } from "./messagesContainer.style";
import { File as TicketFile, Message, Ticket } from '@reporting/rp-types/helpdesk';

const REFRESH_INTERVAL = 30000;

@customElement('rp-helpdesk-messages-container')
export class MessagesContainer extends AppComponent {

	static styles = [style, unsafeCSS(markdownContentStyles)];

	@property({type: Object})
	ticket: Ticket;

	/** State of the auto-refresh toggle; the polling itself runs only while connected and the tab is visible. */
	@state()
	autoRefresh = false;

	private _polling: Polling | null = null;

	@query('og-overlay')
	loader: Overlay;

	@query('#messages')
	messagesRoot: HTMLDivElement;

	@query('og-container')
	msgContainer: Container;

	@property({type: Number})
	scrollHeight: number;

	attachments: TicketFile[];

	constructor() { super(); }

	connectedCallback() {
		super.connectedCallback();
		if (this.autoRefresh) this._startAutoRefresh();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._polling?.stop(); // a detached view (ticket closed, tab switched) must not keep refreshing
	}

	async updated() {
		if (this.ticket) await this.loadMessages();
	}

	// fetch attachments for ticket
	async fetchAttachments() {
		this.attachments = await rp.ms('helpdesk').get<TicketFile[]>(`ticketFiles/${this.ticket.id_request}`, { accounting: this.ticket.accounting });
	}

	async markdownToHtml(content: string): Promise<string> {
		let htmlText = await markdownToHtml(content, {
			// only idz references are rewritten to the download endpoint; absolute URLs (e.g. twemoji CDN) stay untouched
			modifyImageUrl: (url: string) => /^(https?|data|blob):/.test(url)
				? url
				: `${rc.options.defaultServiceOptions.baseUrl}/${this.ticket.accounting}/helpdesk/download/${url}`
		});
		htmlText = htmlText.replace(
			/(\/[^\s"']+\/download\/\d+)\?width=([\d.]+)&amp;height=([\d.]+)/g,
			(match, baseUrl, width, height) => {
			  // height: auto + aspect-ratio keep the proportions when max-width shrinks the image
			  return `${baseUrl}" style="width: ${width}px; height: auto; aspect-ratio: ${width} / ${height};`;
			}
		);

		return htmlText;
	}

	// create attachments element for message
	createAttachmentsElement(idMsg: number): HTMLDivElement {
		const element = document.createElement('div');
		element.classList.add('attachments');
		if (this.attachments.length > 0) {
			const files = this.attachments.filter(f => f.id_message === idMsg);
			for (const file of files) {
				const aFile = document.createElement('a');
				aFile.classList.add('attachment');
				aFile.href = `${rc.options.defaultServiceOptions.baseUrl}/${this.ticket.accounting}/helpdesk/download/${file.idz}`;
				aFile.target = '_blank';
				aFile.innerText = file.file_name;
				element.appendChild(aFile);
			}
		}
		return element;
	}

	// create message element
	async createMessageElement(message: Message, attachments: HTMLDivElement = null): Promise<HTMLDivElement> {
		const htmlText = await this.markdownToHtml(message.text);
		const element = document.createElement('div');
		if (message.sys_message) {
			element.classList.add('system-message');
			element.innerHTML = `<p><i>${formatDateTime(message.date_created)} - ${message.who_created_dv}:</i> ${htmlText.substring(3)}`;
		} else {
			element.classList.add('message');
			if (message.is_sa) element.classList.add('rp');
			const login = message.who_created_dv ?? '';
			element.innerHTML = `
				<div class="message-left">
					<span class="message-avatar" title="${msg(message.is_sa ? 'Reporting team' : 'User')}">
						${helpers.getInitials(login)}
					</span>
					<span class="message-name" title="${login}">${login}</span>
					<span class="message-time">${formatDateTime(message.date_created)}</span>
				</div>
				<div class="message-content">
					<div class="og-markdown-content">${htmlText}</div>
				</div>
			`;
			if (attachments) element.querySelector('.message-content').appendChild(attachments);
		}
		return element;
	}

	// load messages for ticket
	async loadMessages(scrollDown: boolean = true) {
		this.loader.show(this);
		this.messagesRoot.innerHTML = '';
		const messages = await rp.ms('helpdesk').get<Message[]>(`ticketMessages/${this.ticket.id_request}`, { accounting: this.ticket.accounting });
		await this.fetchAttachments();
		if (messages.length > 0) {
			for (const message of messages) {
				const attachmentsElement = this.createAttachmentsElement(message.id_message);
				const messageElement = await this.createMessageElement(message, attachmentsElement);
				this.messagesRoot.appendChild(messageElement);
			}
			this.messagesRoot.querySelectorAll('.og-markdown-content img').forEach((img) => {
				img.addEventListener('load', () => {
					img.classList.add('loaded');
				});
			});
		} else {
			const element = document.createElement('div');
			element.classList.add('no-messages');
			element.innerHTML = msg('The conversation does not contain any messages...');
			this.messagesRoot.appendChild(element);
		}
		if (scrollDown) this.scrollDown();
		setTimeout(() => this.loader.hide(), 100);
	}

	refresh = () => this.loadMessages();

	_autoRefresh = () => {
		this.autoRefresh = !this.autoRefresh;
		if (this.autoRefresh) this._startAutoRefresh();
		else this._polling?.stop();
	}

	private _startAutoRefresh() {
		if (!this.isConnected) return; // started by connectedCallback
		this._polling ??= new Polling(() => this.loadMessages(), REFRESH_INTERVAL);
		this._polling.start();
	}

	scrollDown(smooth: boolean = true, speed: number = 10, stepPx: number = 300) {
		const container = this.msgContainer;
		if (!container?.verticalSlider) return;
		container.updateSliders();
		const endValue = container.scrollHeight;
		if (smooth) {
			let currentValue = container.verticalSlider.value;
			const interval = setInterval(() => {
				if (currentValue >= endValue || !container.verticalSlider) {
					clearInterval(interval);
					return;
				}
				currentValue += stepPx;
				container.scrollVerticalTo(currentValue);
			}, 100 / speed);
		}
		container.scrollVerticalTo(endValue);
		//container.updateSliders();
	}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>
		<div class="head" style="margin: 3px;">
			<og-button
				icon="arrows-rotate"
				color="${ColorTypes.Gray}"
				title="${msg('Refresh')}"
				style="flex: 0; min-width: 25px; font-size: 15px;"
				@click="${() => this.refresh()}">
			</og-button>
			<og-button
				icon="arrows-spin"
				color="${this.autoRefresh ? ColorTypes.Blue : ColorTypes.Gray}"
				id="auto-refresh"
				title="${msg('Refresh every')} ${REFRESH_INTERVAL/1000} ${msg('seconds')}"
				style="flex: 0; min-width: 25px; font-size: 15px;"
				@click="${() => this._autoRefresh()}">
			</og-button>
			<og-button
				icon="angles-down"
				title="${msg('Scroll down')}"
				style="flex: 0; min-width: 25px; font-size: 15px;"
				@click="${() => this.scrollDown(true, 4, 100)}">
			</og-button>
		</div>
        <og-container style="height: 100%;" >
			<div slot="content" id="messages"></div>
		</og-container>
	`;
}
